import { setVideoPlayerModal, setVideoSrc, setVideoSubtitleSrc } from '../../../../../store/workspace/slice';
import { selectVideoSrc, selectVideoSubtitleSrc } from '../../../../../store/workspace/selectors';
import { useDispatch, useSelector } from '../../../../../store/store';
import Spinner from '../../../../../components/Spinner';
import Modal from '../../../../../components/Modal';
import { useEffect, useRef, useState } from 'react';
import styles from './style.module.scss';
import axios from 'axios';

const PLAYBACK_RATES = [1, 1.5, 2, 3, 4];

/** Interview recordings are shipped as SRT; `<track>` only understands WebVTT. */
const srtToVtt = (srt: string) => 'WEBVTT\n\n' + srt
    .replace(/\r/g, '')
    .replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');

export default function VideoPlayer() {
    const dispatch = useDispatch();
    const videoSrc = useSelector(selectVideoSrc);
    const subtitleSrc = useSelector(selectVideoSubtitleSrc);

    const videoRef = useRef<HTMLVideoElement>(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [videoObjectURL, setVideoObjectURL] = useState<string | null>(null);
    const [subtitleObjectURL, setSubtitleObjectURL] = useState<string | null>(null);

    useEffect(() => {
        if (!videoSrc) return;

        let objectURL: string | null = null;
        let cancelled = false;

        const fetchVideo = async () => {
            try {
                setLoading(true);
                setError(false);

                const response = await axios.get(videoSrc, { responseType: 'blob' });
                if (cancelled) return;

                // Fall back to webm when the storage layer omits the content type.
                const blob = new Blob([response.data], { type: response.data.type || 'video/webm' });
                objectURL = URL.createObjectURL(blob);
                setVideoObjectURL(objectURL);
            } catch (e: unknown) {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchVideo();

        return () => {
            cancelled = true;
            if (objectURL) URL.revokeObjectURL(objectURL);
        };
    }, [videoSrc]);

    useEffect(() => {
        if (!subtitleSrc) return;

        let objectURL: string | null = null;
        let cancelled = false;

        const fetchSubtitle = async () => {
            try {
                const response = await fetch(subtitleSrc);
                if (!response.ok || cancelled) return;

                const blob = new Blob([srtToVtt(await response.text())], { type: 'text/vtt' });
                if (cancelled) return;

                objectURL = URL.createObjectURL(blob);
                setSubtitleObjectURL(objectURL);
            } catch (e: unknown) {
                // A missing transcript must not break playback.
            }
        };

        fetchSubtitle();

        return () => {
            cancelled = true;
            if (objectURL) URL.revokeObjectURL(objectURL);
        };
    }, [subtitleSrc]);

    useEffect(() => {
        return () => {
            dispatch(setVideoSrc(null));
            dispatch(setVideoSubtitleSrc(null));
        };
    }, [dispatch]);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    return (
        <Modal
            size='xxl'
            childClassName={styles.wrapper}
            closeFunc={() => dispatch(setVideoPlayerModal(false))}
        >
            <div style={{ paddingTop: '16px' }}>
                {loading && <div className={styles.loading}><Spinner /></div>}

                {error && <div className={styles.error}>
                    Failed to load video. Please try again.
                </div>}

                {!loading && !error && videoObjectURL && <div className={styles.player}>
                    <video
                        controls
                        ref={videoRef}
                        src={videoObjectURL}
                        crossOrigin='anonymous'
                        controlsList='nodownload'
                        style={{ width: '100%', height: '100%' }}
                    >
                        {subtitleObjectURL && <track
                            default
                            kind='subtitles'
                            srcLang='en'
                            label='English'
                            src={subtitleObjectURL}
                        />}
                    </video>
                    <div className={styles.player_speed}>
                        {PLAYBACK_RATES.map(rate => <button
                            key={rate}
                            className={styles.player_speed_btn}
                            data-is-selected={playbackRate === rate}
                            onClick={() => setPlaybackRate(rate)}
                        >
                            {rate}x
                        </button>)}
                    </div>
                </div>}
            </div>
        </Modal>
    );
};
